import { createContext, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { orgSelector } from 'src/redux/selectors';
import { isSameOrgSlug } from 'src/utils/organization.util';
import { getOrganizationPresentation, NEUTRAL_PRESENTATION, Presentation, resolveCopyVariants } from 'src/utils/presentation.util';

/**
 * The admin live preview provides the presentation of the unsaved form (`getDraftPresentation`)
 * here, so every landing section it renders previews the draft rather than the saved org.
 */
export const PresentationPreviewContext = createContext<Presentation | null>(null);

/**
 * NPT-2004 — the current organization's presentation: what its travelers are told on surfaces
 * without a priced rate (landing, FAQ, share copy, program picker). Rate surfaces read
 * `getRatePresentation(commission)` instead.
 *
 * Fails closed: until the route's organization is in the store (the whole store is
 * redux-persisted, so a cold load first renders the previously visited org, NPT-1859) this is
 * the neutral presentation, never the previous org's proceeds or savings copy.
 *
 * `override` (or a PresentationPreviewContext above) is the admin live preview's draft.
 */
export function usePresentation(override?: Presentation | null): Presentation {
   const preview = useContext(PresentationPreviewContext);
   const { organization } = useSelector(orgSelector);
   const { organizationSlug } = useParams();
   const isRouteOrg = !!organization && (!organizationSlug || isSameOrgSlug(organization.slug, organizationSlug));
   return useMemo(
      () => override ?? preview ?? (isRouteOrg ? getOrganizationPresentation(organization) : NEUTRAL_PRESENTATION),
      [override, preview, isRouteOrg, organization],
   );
}

/**
 * The i18n `defaultConfiguration` block (landing hero, How it works steps, program picker helper
 * text) with its three-variant elements resolved for the current presentation. An organization's
 * own configuration still wins over these defaults wherever the caller merges it in.
 */
export function useDefaultConfiguration(params: Record<string, unknown> = {}, override?: Presentation | null): any {
   const [t] = useTranslation();
   const { copyVariant } = usePresentation(override);
   const key = JSON.stringify(params);
   return useMemo(
      () => resolveCopyVariants(t('defaultConfiguration', { returnObjects: true, ...params }) as Record<string, unknown>, copyVariant),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [t, copyVariant, key],
   );
}
